/**
 * Project Header Component
 */

import { ArrowLeft, Edit, MoreVertical, Trash2, Users, Menu } from '@/components/ui/icons';
import { useState } from 'react';

interface ProjectHeaderProps {
  projectName: string;
  projectType: string;
  projectEmoji?: string;
  onBack?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onManageMembers?: () => void;
  onToggleSidebar?: () => void;
  memberCount?: number;
}

export function ProjectHeader({ 
  projectName, 
  projectType, 
  projectEmoji = '📁',
  onBack,
  onEdit,
  onDelete,
  onManageMembers,
  onToggleSidebar,
  memberCount = 0,
}: ProjectHeaderProps) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <header className="bg-white border-b border-surface-border h-[70px] md:h-[93px]">
      <div className="h-full w-full px-3 md:px-6 flex items-center justify-between">
        {/* Left: Menu button (mobile) + Back button + Project info */}
        <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
          {/* Mobile Menu Button */}
          <button 
            onClick={onToggleSidebar}
            className="md:hidden w-9 h-9 flex items-center justify-center hover:bg-surface-secondary rounded-lg transition-colors flex-shrink-0"
            aria-label="Toggle sidebar"
          >
            <Menu className="w-5 h-5 text-gray-700" />
          </button>

          {/* Back Button */}
          <button 
            onClick={onBack}
            className="w-6 h-6 flex items-center justify-center hover:bg-surface-secondary rounded transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-700" />
          </button>
          
          {/* Project Icon - hidden on mobile */}
          <div className="hidden sm:flex w-10 h-10 bg-brand-100 rounded-[10px] items-center justify-center flex-shrink-0">
            <span className="text-[26px] leading-none">{projectEmoji}</span>
          </div>
          
          {/* Project Name & Type */}
          <div className="min-w-0">
            <h1 className="text-base md:text-2xl font-medium text-gray-900 tracking-tight leading-tight md:leading-9 truncate">
              {projectName}
            </h1>
            <p className="text-sm md:text-base text-gray-600 capitalize tracking-tight hidden sm:block">
              {projectType} Project
            </p>
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-1 md:gap-3 flex-shrink-0">
          {/* Team members button */}
          <button 
            onClick={onManageMembers}
            className="h-9 md:h-10 px-2 md:px-3 flex items-center gap-1 md:gap-2 rounded-[10px] hover:bg-surface-secondary transition-colors"
            title="Manage team members"
          >
            <Users className="w-4 h-4 md:w-5 md:h-5 text-gray-700" />
            {memberCount > 0 && (
              <span className="text-xs md:text-sm text-gray-600">{memberCount}</span>
            )}
          </button>

          {/* Edit button */}
          <button 
            onClick={onEdit}
            className="h-9 md:h-10 px-2 md:px-4 flex items-center gap-1 md:gap-2 rounded-[10px] hover:bg-surface-secondary transition-colors"
          >
            <Edit className="w-4 h-4 text-gray-700" />
            <span className="hidden md:inline text-base text-gray-700">Edit</span>
          </button>

          {/* More menu */}
          <div className="relative">
            <button 
              onClick={() => setShowMenu(!showMenu)}
              className="w-9 h-9 flex items-center justify-center rounded-[10px] hover:bg-surface-secondary transition-colors"
            >
              <MoreVertical className="w-5 h-5 text-gray-700" />
            </button>
            
            {showMenu && (
              <>
                {/* Backdrop to close menu */}
                <div 
                  className="fixed inset-0 z-40" 
                  onClick={() => setShowMenu(false)} 
                />
                <div className="absolute right-0 top-11 w-48 bg-white border border-surface-border rounded-[10px] shadow-dropdown py-1 z-50">
                  <button 
                    onClick={() => {
                      setShowMenu(false);
                      onDelete?.();
                    }}
                    className="w-full px-4 py-2.5 flex items-center gap-2 text-[#e7000b] hover:bg-surface-secondary transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="text-base">Delete Project</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
